<template>
    <div class="landing-page-sell container py-5">
        <template v-if="!renderChildren">
            <b-row class="top-section my-5">
                <b-col>
                    <b-row>
                        <b-col class="text-center">
                            <p class="title">
                                You're in control of your home sale
                            </p>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col class="text-center">
                            <p class="subtitle">
                                Let's explore your options and pick the one that works best for you.
                            </p>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-row
                v-if="loading"
                class="loader-wrapper p-2"
            >
                <b-col>
                    <element-loader :loading="true" />
                </b-col>
            </b-row>
            <b-row
                v-else
            >
                <b-col>
                    <sell-cards-mobile
                        v-if="isMobile"
                        :cards="cards"
                    />

                    <sell-cards
                        v-else
                        :sell="sellAppResource"
                        :cards="cards"
                    />
                </b-col>
            </b-row>
        </template>
        <router-view
            v-if="renderChildren"
            :landing-page="landingPage"
        />
    </div>
</template>

<script>
    import './Sell.scss';
    import LandingPageSellSectionAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageSellSectionAppResource";
    import ShowNotificationMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin";
    import AppResourceSearch from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceSearch";
    import ApiFilter from "@milestoneslabs/milestones-js-engine/src/Api/ApiFilter";
    import SellCards from "../../../components/LandingPage/Sell/SellCards/SellCards";
    import {SELL_IMAGES} from "../../../helpers/images";
    import SellCardsMobile from "../../../components/LandingPage/Sell/SellCardsMobile/SellCardsMobile";
    import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
    import {newProjectUrl} from "../../../components/helpers/externalRoutes";
    import {HOME_SALE} from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceTypeAppResource";
    import WorkspaceOrganizationMemberAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/WorkspaceOrganizationMemberAppResource";
    import {INTERSTITIAL_MODAL_TYPES, showInterstitialModal} from "../../../helpers/modals";
    import LandingPageAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/LandingPageAppResource";

    export default {
        name: "Sell",

        components: {SellCardsMobile, SellCards},

        mixins: [
            ShowNotificationMixin,
            IsMobileMixin,
        ],

        data() {
            return {
                landingPageUuid: window.landingPageUuid,
                sellAppResource: new LandingPageSellSectionAppResource({}),
                projectTypeAppResource: new WorkspaceOrganizationMemberAppResource({}),
                loading: true,
            };
        },

        props: {
            landingPage: {
                type: LandingPageAppResource,
                required: true,
            },
        },

        computed: {
            renderChildren(){
              return this.$route.name === 'landing-page.sell.get-instant-cash'
            },
            cards() {
                return [
                    {
                        enabled: this.sellAppResource.hasHomeList,
                        title: 'Maximize Sale Price',
                        subtitle: 'I\'ll list your property and get you the best sale price.',
                        icon: SELL_IMAGES.MAXIMIZE_SALE_PRICE,
                        items: [
                            {
                                title: 'Get top dollar',
                                content: 'Put your home on the market and sell for a high price.'
                            },
                            {
                                title: 'Attract more buyers',
                                content: 'Get an advance to make renovations.',
                            },
                            {
                                title: 'Guided by local experts',
                                content: 'From choosing the right price to closing, I’m here from start to finish.',
                            },
                        ],
                        button: {
                            text: 'List Your Home',
                            color: 'purple',
                            action: () => {
                                if (!this.landingPageUuid) {
                                    return;
                                }

                                showInterstitialModal(
                                    INTERSTITIAL_MODAL_TYPES.GET_STARTED_SELL,
                                    {
                                        firstName: this.landingPage.firstName,
                                        lastName: this.landingPage.lastName,
                                        displayName: this.landingPage.displayName,
                                        avatar: this.landingPage.avatar,
                                    },
                                    {
                                        landingPageUuid: this.landingPage.uuid,
                                        type: this.projectTypeAppResource.uuid,
                                    }
                                );
                            }
                        },
                        new: false,
                    },
                    {
                        enabled: this.sellAppResource.hasCashOffer,
                        title: 'Sell Quick & Easy',
                        subtitle: 'Get a competitive cash offer and enjoy a stress-free sale without listing.',
                        icon: SELL_IMAGES.SELL_QUICK,
                        items: [
                            {
                                title: 'Explore cash offers',
                                content: 'Sell directly and get paid in a matter of days.'
                            },
                            {
                                title: 'Skip showings and repairs',
                                content: 'Get a virtual home assessment to get your cash offer, and sell the home as-is.'
                            },
                            {
                                title: 'Move on your timeline',
                                content: 'Choose your closing date and avoid moving twice or carrying two mortgages.'
                            },
                        ],
                        button: {
                            text: 'Explore Cash Offers',
                            color: 'primary',
                            action: () => {
                                this.$router.push({name: 'landing-page.sell.get-instant-cash'});
                            }
                        },
                        new: false,
                    },
                    {
                        enabled: this.sellAppResource.hasTradeIn,
                        title: 'Buy Before You Sell',
                        subtitle: 'Use the equity  in your current home as a down payment for new home.',
                        icon: SELL_IMAGES.BUY_BEFORE_SELL,
                        items: [
                            {
                                title: 'Find the home you want',
                                content: 'Search for your next home and make an offer.'
                            },
                            {
                                title: 'Buy the house and move in',
                                content: 'Use the equity from your existing home as a down payment.'
                            },
                            {
                                title: 'Sell for full price',
                                content: 'Move in to your new home, then list and sell your old home for full price.'
                            },
                        ],
                        button: {
                            text: 'Trade In Your Home',
                            color: 'danger',
                            action: () => {
                                this.$router.push({name: 'landing-page.trade'});
                            }
                        },
                        new: true,
                    },
                ];
            }
        },

        methods: {
            async fetchSellSection() {
                if (!landingPageUuid) {
                    return;
                }

                let sellSearch = new AppResourceSearch('landing-page-sell-section');

                await sellSearch.setSearchData({
                    filter: new ApiFilter({
                        params: {
                            landingPageUuid: this.landingPageUuid
                        }
                    })
                });

                try {
                    await sellSearch.search();

                    if (sellSearch.appResourceCollection.isNotEmpty) {
                        this.sellAppResource = sellSearch.appResourceCollection.getFirst();
                    }
                } catch (e) {
                    console.warn(e);
                    this.showNotification({
                        message: `Something went wrong while fetching sell section! Please try again later!`,
                        type: 'error',
                    });
                }
            },

            async fetchSellProjectType() {
                let projectTypeSearch = new AppResourceSearch('workspace-type');

                await projectTypeSearch.setSearchData({
                    filter: new ApiFilter({
                        params: {
                            type: HOME_SALE,
                        }
                    }),
                });

                try {
                    await projectTypeSearch.search();

                    if (projectTypeSearch.appResourceCollection.isNotEmpty) {
                        this.projectTypeAppResource = projectTypeSearch.appResourceCollection.getFirst();
                    }
                } catch (e) {
                    console.warn(e);
                }
            }
        },

        async beforeMount() {
            await Promise.all([
                this.fetchSellSection(),
                this.fetchSellProjectType(),
            ]);

            this.loading = false;
        }
    };
</script>
